import type { EnvironmentId, LiveSubagentId, ThreadId } from "@t3tools/contracts";
import { FileDiffIcon, ListTreeIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useMediaQuery } from "../hooks/useMediaQuery";
import { i18n } from "../i18n";
import { RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY } from "../rightPanelLayout";
import { BranchToolbarBranchSelector } from "./BranchToolbarBranchSelector";
import { BranchToolbarEnvModeSelector } from "./BranchToolbarEnvModeSelector";
import { SourceControlActions, useSourceControlController } from "./GitActionsControl";
import {
  LiveSubagentSummaryContent,
  useLiveSubagentRoster,
  type LiveSubagentRosterTarget,
} from "./LiveSubagentSummary";
import { useRunContextController } from "./RunContextController";
import { DiffStatLabel } from "./chat/DiffStatLabel";
import { Button } from "./ui/button";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "./ui/popover";
import { Sheet, SheetHeader, SheetPanel, SheetPopup, SheetTitle, SheetTrigger } from "./ui/sheet";

const SUMMARY_BRANCH_MENU_OWNER = "thread-summary";

interface ThreadSummaryProps {
  readonly scopeKey: string;
  readonly diffAvailable: boolean;
  readonly onOpenDiff: () => void;
  readonly liveSubagents?:
    | (LiveSubagentRosterTarget & {
        readonly onSelectAgent: (agentId: LiveSubagentId) => void;
      })
    | null;
}

interface SummaryPresentationState {
  readonly scopeKey: string;
  readonly open: boolean;
  readonly sourceControlMenuOpen: boolean;
}

interface SummaryOpenChangeDetails {
  readonly reason?: string;
  readonly cancel?: () => void;
}

function ThreadSummaryEnvironment(props: {
  readonly diffAvailable: boolean;
  readonly narrow: boolean;
  readonly onOpenDiff: () => void;
}) {
  const runContext = useRunContextController();
  const sourceControl = useSourceControlController();
  const status = sourceControl.statusSnapshot.status;
  const isDetached = status?.isRepo === true && status.refName === null;
  const additions = status?.workingTree.insertions ?? 0;
  const deletions = status?.workingTree.deletions ?? 0;
  const hasChanges = additions > 0 || deletions > 0;
  const changesLabel = i18n.t("threadSummary.changesCount", { additions, deletions });
  const changesDisabledReason = !props.diffAvailable
    ? i18n.t("threadSummary.diffAvailableAfterStart")
    : null;

  return (
    <section aria-label={i18n.t("threadSummary.environment")} className="px-2 pb-1">
      <div className="space-y-0.5">
        <button
          type="button"
          aria-disabled={changesDisabledReason !== null}
          aria-label={
            hasChanges
              ? i18n.t("threadSummary.openChanges", { additions, deletions })
              : i18n.t("threadSummary.changes")
          }
          className="flex min-h-10 w-full min-w-0 items-center justify-between gap-4 rounded-lg px-3 py-2 text-left outline-none not-aria-disabled:hover:bg-accent/70 focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-64"
          title={changesDisabledReason ?? changesLabel}
          onClick={() => {
            if (changesDisabledReason === null) {
              props.onOpenDiff();
            }
          }}
        >
          <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground/90">
            <FileDiffIcon className="size-4 text-foreground/80" aria-hidden />
            {i18n.t("threadSummary.changes")}
          </span>
          <span className="flex min-w-0 items-center justify-end gap-2 text-xs">
            {sourceControl.statusSnapshot.error ? (
              <span className="truncate text-destructive" role="status">
                {i18n.t("threadSummary.statusUnavailable")}
              </span>
            ) : status === null ? (
              <span className="text-muted-foreground" role="status">
                {i18n.t("threadSummary.loadingStatus")}
              </span>
            ) : !status.isRepo ? (
              <span className="text-muted-foreground">{i18n.t("threadSummary.notRepository")}</span>
            ) : hasChanges ? (
              <DiffStatLabel additions={additions} deletions={deletions} />
            ) : (
              <span className="text-muted-foreground">{i18n.t("threadSummary.noChanges")}</span>
            )}
            {changesDisabledReason ? (
              <span className="sr-only">{changesDisabledReason}</span>
            ) : null}
          </span>
        </button>
        <div>
          <BranchToolbarEnvModeSelector
            envLocked={runContext.envModeLocked}
            effectiveEnvMode={runContext.effectiveEnvMode}
            activeWorktreePath={runContext.activeWorktreePath}
            onEnvModeChange={runContext.onEnvModeChange}
            lockedDescription={i18n.t("threadSummary.workspaceLockedDescription")}
            previousWorktreeLabel={runContext.previousWorktreeLabel}
            onUsePreviousWorktree={runContext.onUsePreviousWorktree}
          />
        </div>
        <div>
          <BranchToolbarBranchSelector
            menuOwner={SUMMARY_BRANCH_MENU_OWNER}
            presentation="summary"
            popupAlign={props.narrow ? "end" : "start"}
            popupSide={props.narrow ? "bottom" : "left"}
            popupSideOffset={props.narrow ? 6 : 10}
          />
        </div>
        {isDetached ? (
          <p className="mx-3 py-2 text-[11px] leading-relaxed text-warning" role="status">
            {i18n.t("threadSummary.detachedHead")}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function ThreadSummarySourceControl(props: {
  readonly onDialogWillOpen: () => void;
  readonly onMenuOpenChange: (open: boolean) => void;
}) {
  return (
    <section
      aria-label={i18n.t("threadSummary.sourceControl")}
      className="px-2 pb-2"
      data-thread-summary-source-control
    >
      <SourceControlActions
        presentation="summary"
        onDialogWillOpen={props.onDialogWillOpen}
        onMenuOpenChange={props.onMenuOpenChange}
      />
    </section>
  );
}

function ThreadSummaryLiveSubagents(props: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly onSelectAgent: (agentId: LiveSubagentId) => void;
}) {
  const roster = useLiveSubagentRoster({
    environmentId: props.environmentId,
    threadId: props.threadId,
  });
  const agents = roster.data?.status === "available" ? roster.data.agents : [];
  const runtimeEnded =
    roster.data?.status === "expired" ||
    (roster.data?.status === "unavailable" && roster.data.reason === "runtime-not-live");

  if (runtimeEnded) {
    return null;
  }

  return (
    <section
      aria-labelledby="thread-summary-live-subagents-heading"
      className="mx-4 border-t border-border/60 pt-3 pb-3"
      data-thread-summary-live-subagents
    >
      <div className="flex items-center justify-between gap-3 px-1 pb-2">
        <h3
          id="thread-summary-live-subagents-heading"
          className="text-sm font-semibold text-muted-foreground"
        >
          {i18n.t("liveSubagents.liveSubagents")}
        </h3>
        {roster.data?.status === "available" ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {i18n.t(
              agents.length === 1 ? "liveSubagents.agentCountOne" : "liveSubagents.agentCountOther",
              { count: agents.length },
            )}
          </span>
        ) : null}
      </div>
      <div className="flex max-h-64 min-w-0 flex-col">
        <LiveSubagentSummaryContent roster={roster} onSelectAgent={props.onSelectAgent} />
      </div>
    </section>
  );
}

export function ThreadSummary(props: ThreadSummaryProps) {
  const runContext = useRunContextController();
  const sourceControl = useSourceControlController();
  const narrow = useMediaQuery(RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const suppressSummaryFocusReturnRef = useRef(false);
  const [presentation, setPresentation] = useState<SummaryPresentationState>({
    scopeKey: props.scopeKey,
    open: false,
    sourceControlMenuOpen: false,
  });
  if (presentation.scopeKey !== props.scopeKey) {
    setPresentation({
      scopeKey: props.scopeKey,
      open: false,
      sourceControlMenuOpen: false,
    });
  }
  const open = presentation.scopeKey === props.scopeKey && presentation.open;
  const setOpen = useCallback(
    (nextOpen: boolean, eventDetails?: SummaryOpenChangeDetails) => {
      const branchMenuOpen =
        runContext.branch.isMenuOpen && runContext.branch.menuOwner === SUMMARY_BRANCH_MENU_OWNER;
      if (
        !nextOpen &&
        eventDetails?.reason === "escape-key" &&
        (presentation.sourceControlMenuOpen || branchMenuOpen)
      ) {
        eventDetails.cancel?.();
        return;
      }
      if (!nextOpen) {
        runContext.branch.setMenuOpen(SUMMARY_BRANCH_MENU_OWNER, false);
      } else {
        sourceControl.statusSnapshot.refresh();
      }
      suppressSummaryFocusReturnRef.current = false;
      setPresentation({
        scopeKey: props.scopeKey,
        open: nextOpen,
        sourceControlMenuOpen: false,
      });
    },
    [
      presentation.sourceControlMenuOpen,
      props.scopeKey,
      runContext.branch,
      sourceControl.statusSnapshot,
    ],
  );
  useEffect(
    () => () => {
      runContext.branch.setMenuOpen(SUMMARY_BRANCH_MENU_OWNER, false);
    },
    [props.scopeKey],
  );
  const handleOpenDiff = useCallback(() => {
    setOpen(false);
    props.onOpenDiff();
  }, [props.onOpenDiff, setOpen]);
  const handleSelectLiveSubagent = useCallback(
    (agentId: LiveSubagentId) => {
      setOpen(false);
      props.liveSubagents?.onSelectAgent(agentId);
    },
    [props.liveSubagents, setOpen],
  );
  const handleSourceControlMenuOpenChange = useCallback(
    (sourceControlMenuOpen: boolean) => {
      setPresentation((current) =>
        current.scopeKey === props.scopeKey
          ? { ...current, sourceControlMenuOpen }
          : {
              scopeKey: props.scopeKey,
              open: false,
              sourceControlMenuOpen: false,
            },
      );
    },
    [props.scopeKey],
  );
  const handleSourceControlDialogWillOpen = useCallback(() => {
    sourceControl.prepareDialogFocusReturn(() => triggerRef.current);
    runContext.branch.setMenuOpen(SUMMARY_BRANCH_MENU_OWNER, false);
    suppressSummaryFocusReturnRef.current = true;
    setPresentation({
      scopeKey: props.scopeKey,
      open: false,
      sourceControlMenuOpen: false,
    });
  }, [props.scopeKey, runContext.branch, sourceControl]);
  const resolveSummaryFocusReturn = useCallback(
    () => (suppressSummaryFocusReturnRef.current ? false : triggerRef.current),
    [],
  );

  if (!runContext.hasActiveContext) {
    return null;
  }

  const content = open ? (
    <>
      <ThreadSummaryEnvironment
        diffAvailable={props.diffAvailable}
        narrow={narrow}
        onOpenDiff={handleOpenDiff}
      />
      <ThreadSummarySourceControl
        onDialogWillOpen={handleSourceControlDialogWillOpen}
        onMenuOpenChange={handleSourceControlMenuOpenChange}
      />
      {props.liveSubagents ? (
        <ThreadSummaryLiveSubagents
          environmentId={props.liveSubagents.environmentId}
          threadId={props.liveSubagents.threadId}
          onSelectAgent={handleSelectLiveSubagent}
        />
      ) : null}
    </>
  ) : null;
  const trigger = (
    <Button
      ref={triggerRef}
      type="button"
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={i18n.t("threadSummary.open")}
      className="shrink-0 [-webkit-app-region:no-drag]"
      data-thread-summary-trigger
      size="icon-sm"
      variant="ghost"
      title={i18n.t("threadSummary.summary")}
    >
      <ListTreeIcon className="size-3.5 text-foreground" aria-hidden />
    </Button>
  );

  if (narrow) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger render={trigger} />
        <SheetPopup
          side="right"
          finalFocus={resolveSummaryFocusReturn}
          aria-label={i18n.t("threadSummary.summary")}
          className="w-[min(calc(100vw-1rem),22rem)] max-w-[22rem] wco:mt-[env(titlebar-area-height)] wco:h-[calc(100%-env(titlebar-area-height))] wco:max-h-[calc(100%-env(titlebar-area-height))]"
        >
          <SheetHeader className="px-5 pt-5 pb-2">
            <SheetTitle className="text-sm text-muted-foreground">
              {i18n.t("threadSummary.environment")}
            </SheetTitle>
          </SheetHeader>
          <SheetPanel className="p-0">{content}</SheetPanel>
        </SheetPopup>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      {open ? (
        <PopoverPopup
          side="bottom"
          align="end"
          sideOffset={7}
          initialFocus={false}
          finalFocus={resolveSummaryFocusReturn}
          aria-label={i18n.t("threadSummary.summary")}
          className="w-[min(22rem,calc(100vw-1.5rem))] max-w-none rounded-2xl border-border/60 shadow-xl/10 before:rounded-[calc(var(--radius-2xl)-1px)]"
          viewportClassName="overflow-hidden! p-0!"
        >
          <div className="flex max-h-[min(38rem,calc(100vh-5rem))] min-h-0 flex-col overflow-y-auto overscroll-contain">
            <div className="shrink-0 px-5 pt-4 pb-2">
              <PopoverTitle className="text-sm text-muted-foreground">
                {i18n.t("threadSummary.environment")}
              </PopoverTitle>
            </div>
            {content}
          </div>
        </PopoverPopup>
      ) : null}
    </Popover>
  );
}
